import { Box } from '@chakra-ui/react'
import { buildQrSvg } from '../../lib/qr'

interface QrCodeProps {
  /** Any URL/string to encode. Generic by design — shared code today, per-person/P2 later. */
  value: string
  /** Rendered SVG pixel size. Default 256. */
  size?: number
  /** Accessible label (role="img" name). */
  title?: string
}

/**
 * [CANV-4] Generic inline-SVG QR renderer. Modules are fixed black-on-white in both app themes
 * (a QR must stay high-contrast to scan reliably — do NOT tokenize the module color); only the
 * surrounding plate padding is themed. Self-contained (vendored encoder, no npm dep, no fetch).
 *
 * dangerouslySetInnerHTML note: the frontend rule bans it with USER/CSV-sourced content. Here the
 * markup is entirely machine-generated by buildQrSvg from a fixed template plus a numeric module
 * grid; `value` is fed to the encoder as DATA (it becomes QR modules, never markup) and `title` is
 * XML-escaped before injection. This is a safe, self-generated SVG.
 */
export function QrCode({ value, size = 256, title }: QrCodeProps) {
  let svg = buildQrSvg(value, { size })
  if (title) {
    svg = svg.replace('role="img">', `role="img"><title>${escapeXml(title)}</title>`)
  }
  return (
    <Box
      // No bg token needed: buildQrSvg paints its own white rect across the full viewBox, including
      // the quiet zone, so the code stays black-on-white for scanning in BOTH themes. The padding is
      // just breathing room around that self-contained white plate.
      p={2}
      borderRadius="md"
      display="inline-block"
      lineHeight={0}
      dangerouslySetInnerHTML={{ __html: svg }}
    />
  )
}

function escapeXml(s: string): string {
  return s.replace(/[<>&"']/g, (c) =>
    c === '<' ? '&lt;' : c === '>' ? '&gt;' : c === '&' ? '&amp;' : c === '"' ? '&quot;' : '&apos;',
  )
}
